import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Eye, Heart, MessageCircle, Users, IndianRupee, Plus, BarChart3, X } from "lucide-react";
import { apiGet, client, PLATFORMS } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import PostDetailModal from "@/components/PostDetailModal";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

function compact(n) {
  if (n == null) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return Math.round(n).toLocaleString();
}

function Sparkline({ data, height = 60 }) {
  if (!data || data.length === 0) return null;
  const w = 800;
  const h = height;
  const max = Math.max(1, ...data.map((d) => d.views));
  const step = data.length > 1 ? w / (data.length - 1) : 0;
  const pts = data.map((d, i) => [i * step, h - (d.views / max) * (h - 8) - 4]);
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ overflow: "visible" }} data-testid="analytics-sparkline">
      <defs>
        <linearGradient id="brassFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(216,168,87,0.35)" />
          <stop offset="100%" stopColor="rgba(216,168,87,0)" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#brassFill)" />
      <path d={path} fill="none" stroke="var(--seal)" strokeWidth="1.6" />
    </svg>
  );
}

function MetricCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">{label}</div>
        <Icon size={14} style={{ color: SEAL }} />
      </div>
      <div className="font-heading text-4xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
        {value}
      </div>
      {sub && <div className="text-xs mt-1" style={{ color: INK_55 }}>{sub}</div>}
    </div>
  );
}

function AddMetricModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    platform: "youtube_long",
    recorded_for: new Date().toISOString().slice(0, 10),
    views: 0,
    likes: 0,
    comments: 0,
    shares: 0,
    saves: 0,
    subscribers_gained: 0,
    revenue: 0,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await client.post("/metrics", {
        ...form,
        views: +form.views,
        likes: +form.likes,
        comments: +form.comments,
        shares: +form.shares,
        saves: +form.saves,
        subscribers_gained: +form.subscribers_gained,
        revenue: +form.revenue,
      });
      onSaved?.();
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose} data-testid="metric-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[92vh] overflow-y-auto" style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }} data-testid="metric-modal">
        <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10" style={{ background: "var(--surface)", borderBottom: "1px solid var(--ink-22)" }}>
          <h3 className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>Log a post</h3>
          <button onClick={onClose} className="btn-ghost" data-testid="metric-modal-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="eyebrow block mb-2">Post title</label>
            <input data-testid="metric-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="input" placeholder="e.g., Morning routine video" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Platform</label>
              <select data-testid="metric-platform" value={form.platform} onChange={(e) => set("platform", e.target.value)} className="input">
                {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-2">Date</label>
              <input data-testid="metric-date" type="date" value={form.recorded_for} onChange={(e) => set("recorded_for", e.target.value)} className="input" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { k: "views", label: "Views" },
              { k: "likes", label: "Likes" },
              { k: "comments", label: "Comments" },
              { k: "shares", label: "Shares" },
              { k: "saves", label: "Saves" },
              { k: "subscribers_gained", label: "New subs" },
            ].map(({ k, label }) => (
              <div key={k}>
                <label className="eyebrow block mb-2">{label}</label>
                <input data-testid={`metric-${k}`} type="number" min="0" value={form[k]} onChange={(e) => set(k, e.target.value)} className="input" />
              </div>
            ))}
          </div>
          <div>
            <label className="eyebrow block mb-2">Revenue (optional)</label>
            <input data-testid="metric-revenue" type="number" min="0" step="0.01" value={form.revenue} onChange={(e) => set("revenue", e.target.value)} className="input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 sticky bottom-0" style={{ background: "var(--surface)", borderTop: "1px solid var(--ink-22)" }}>
          <button onClick={onClose} className="btn-secondary" data-testid="metric-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !form.title.trim()} className="btn-primary disabled:opacity-50" data-testid="metric-save">
            {saving ? "Saving…" : "Save metric"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [range, setRange] = useState(30);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  const load = async () => {
    const [s, r] = await Promise.all([
      apiGet("/metrics/summary", { days: range }),
      apiGet("/metrics", { days: range }),
    ]);
    setSummary(s);
    setRows(r);
  };
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const platformRows = useMemo(() => {
    if (!summary?.by_platform) return [];
    return Object.entries(summary.by_platform)
      .map(([pid, v]) => {
        const meta = PLATFORMS.find((p) => p.id === pid);
        return { pid, label: meta?.label || pid, ...v };
      })
      .sort((a, b) => b.views - a.views);
  }, [summary]);

  const totals = summary?.totals;
  const hasData = (rows?.length || 0) > 0;

  return (
    <div className="max-w-6xl mx-auto" data-coach="analytics-root">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> v. Analytics</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            What&apos;s <span className="font-display-italic">landing</span>.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full overflow-hidden" style={{ border: "1px solid var(--ink-22)" }}>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setRange(d)}
                data-testid={`analytics-range-${d}`}
                className="px-3.5 py-1.5 text-xs transition"
                style={{
                  background: range === d ? "var(--ink)" : "transparent",
                  color: range === d ? "var(--bg)" : INK_55,
                }}
              >
                {d}d
              </button>
            ))}
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="analytics-add-btn">
            <Plus size={14} /> Log post
          </button>
        </div>
      </div>

      {!hasData ? (
        <EmptyState
          testid="analytics-empty"
          icon={BarChart3}
          eyebrow="Analytics"
          title="Numbers tell a"
          titleItalic="story."
          body="Log views, likes and subscribers per post and PintaFlow will weave them into a trend you can actually read. Start by logging one post."
          primaryLabel="Log your first post"
          onPrimary={() => setShowAdd(true)}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <MetricCard icon={Eye} label="i. Views" value={compact(totals?.views)} sub={`${totals?.posts || 0} posts`} testid="metric-card-views" />
            <MetricCard icon={Heart} label="ii. Engagement" value={`${totals?.engagement_rate || 0}%`} sub="likes + comments + shares + saves" testid="metric-card-engagement" />
            <MetricCard icon={Users} label="iii. New subs" value={compact(totals?.subscribers_gained)} sub={`last ${range} days`} testid="metric-card-subs" />
            <MetricCard icon={IndianRupee} label="iv. Revenue" value={`₹${compact(totals?.revenue)}`} sub="self-reported" testid="metric-card-revenue" />
          </div>

          <div className="card mt-4">
            <div className="flex items-center justify-between mb-5">
              <div className="eyebrow">Views · last {range} days</div>
              <TrendingUp size={14} style={{ color: SEAL }} />
            </div>
            <Sparkline data={summary?.series || []} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="card">
              <div className="eyebrow mb-5">By platform</div>
              {platformRows.length === 0 ? (
                <div className="text-sm" style={{ color: INK_55 }}>No platform data yet.</div>
              ) : (
                <div className="space-y-4" data-testid="analytics-by-platform">
                  {platformRows.map((p) => {
                    const max = platformRows[0].views || 1;
                    const pct = Math.round((p.views / max) * 100);
                    return (
                      <div key={p.pid}>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span style={{ color: INK_72 }}>{p.label}</span>
                          <span style={{ color: INK_55 }}>{compact(p.views)} views · {p.posts} posts</span>
                        </div>
                        <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--ink-22)" }}>
                          <div className="h-full transition-all" style={{ width: `${pct}%`, background: SEAL }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-5">
                <div className="eyebrow">Recent posts</div>
                <Link to="/posts" className="text-[11px] hover:opacity-80" style={{ color: SEAL, touchAction: "manipulation" }} data-testid="analytics-view-all-posts">
                  View all →
                </Link>
              </div>
              <div className="space-y-1" style={{ borderTop: "1px solid var(--ink-22)" }} data-testid="analytics-recent-posts">
                {rows.slice(-6).reverse().map((m) => {
                  const meta = PLATFORMS.find((p) => p.id === m.platform);
                  return (
                    <button
                      key={m.id}
                      onClick={() => setSelectedPost(m.id)}
                      className="py-3 flex items-center justify-between gap-3 w-full text-left transition hover:opacity-80"
                      style={{ borderBottom: "1px solid var(--ink-22)", touchAction: "manipulation" }}
                      data-testid={`analytics-post-${m.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate" style={{ color: INK }}>{m.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: INK_55 }}>
                          {meta?.short || m.platform} · {m.recorded_for}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm" style={{ color: INK }}>{compact(m.views)}</div>
                        <div className="text-[10px]" style={{ color: INK_55 }}>{compact(m.likes)} likes</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {showAdd && <AddMetricModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {selectedPost && (
        <PostDetailModal postId={selectedPost} onClose={() => setSelectedPost(null)} onChanged={() => load()} />
      )}
    </div>
  );
}
